import { itemField, record, upper, definitionNotHeld, handBack, type JanusCtx, type Row } from '../ability/context.js';
import { inventoryField } from '../../shared/array-types.js';
import { removeStatusFromRow, statusRows } from '../statuses/expiry.js';
import { statusScalar } from '../actions/activity.js';
import { readInventoryRows } from '../compile/inventory-raw-readers.js';

export interface MonsterBitRemoval {
  inventory_id: string;
  before: { inventory_deleted: number; item_qty: number };
}

/** The browser validates its fixed write shape during the dry run, before any real dice. */
export function assertMonsterBitRemovals(value: unknown): asserts value is MonsterBitRemoval[] {
  if (!Array.isArray(value)) handBack('commit:monster-bit-removal-shape');
  const seen = new Set<string>();
  for (const entry of value) {
    const row = record(entry), before = record(row?.before);
    const id = row?.inventory_id;
    if (!row || Object.keys(row).length !== 2 || !before || Object.keys(before).length !== 2
        || typeof id !== 'string' || !/^[0-9A-F]{8}(?:-[0-9A-F]{4}){3}-[0-9A-F]{12}$/.test(id)
        || before.inventory_deleted !== 0 || !Number.isInteger(before.item_qty) || seen.has(id))
      handBack('commit:monster-bit-removal-shape');
    seen.add(id);
  }
}

/** Retire the harvest tail on the held rows; corpse/bones/spill decisions belong to the death lane. */
export function removeMonsterBits(ctx: JanusCtx, target: Row): void {
  if (!ctx.read.has(target, 'monster_bit')) return;
  const read = ctx.read.inventoryField ?? inventoryField;
  const held = new Set<Row>();
  const aliases = new Map<string, Row[]>();
  const visit = (row: Row): void => {
    if (held.has(row)) return;
    held.add(row);
    const id = upper(read(row, 'inventory_id'));
    if (id) { const copies = aliases.get(id) ?? []; copies.push(row); aliases.set(id, copies); }
    for (const key of ['inventory', 'inventory_in_container', 'encounter_inventories_arr'] as const)
      for (const child of readInventoryRows(row, key)) visit(child);
  };
  for (const row of [target, ctx.encounter, ...ctx.members.values()]) visit(row);
  const retired = new Set<string>();
  for (const entry of ctx.read.entries(target, 'inventory')) {
    const candidate = record(entry);
    if (!candidate) continue;
    // Layer readers may return copies; retire the held row so subsequent effects see this cleanup.
    const id = upper(read(candidate, 'inventory_id'));
    const copies = aliases.get(id) ?? [candidate];
    const part = copies[0]!;
    if (Number(read(part, 'inventory_deleted') ?? 0) !== 0) continue;
    const templateId = upper(read(part, 'inventory_item_id'));
    let type = itemField(part, 'item_type');
    if (type == null) {
      const definition = ctx.read.definition(templateId);
      if (!definition) definitionNotHeld('heal_target_arr:rotting-inventory-definition-not-held', templateId);
      type = itemField(definition, 'item_type');
    }
    if (type !== 'monster_bit') continue;
    const removals = ctx.monsterBitRemovals ??= [];
    if (removals.some(row => row.inventory_id === id)) continue;
    removals.push({ inventory_id: id, before: { inventory_deleted: Number(read(part, 'inventory_deleted') ?? 0),
      item_qty: Number(read(part, 'item_qty') ?? 1) } });
    retired.add(id);
    for (const copy of copies) {
      Object.assign(record(copy.inventory) ?? copy, { inventory_deleted: 1, item_qty: 0 });
      held.add(copy);
    }
  }
  if (!retired.size) return;
  // Bulk target retirement fires neither loss listeners nor connected-peer cascades. Caster aliases can
  // outlive an inline target projection, so collect their exact ids before changing any held lists.
  const statuses = new Set<string>();
  for (const row of held) for (const side of ['target', 'caster'] as const) for (const status of statusRows(row, side))
    if ((side === 'target' && retired.has(upper(read(row, 'inventory_id'))))
      || retired.has(upper(statusScalar(status, 'status_target_id'))))
      statuses.add(upper(statusScalar(status, 'status_id')));
  for (const row of held) for (const id of statuses) if (id) removeStatusFromRow(row, id);
  ctx.statusesAdded = ctx.statusesAdded.filter(status => !retired.has(upper(status.target)));
}
